import { useEffect, useState } from "react";

import { useMutation } from "@apollo/client";
import {
  Button,
  Callout,
  FormGroup,
  Icon,
  InputGroup,
} from "@blueprintjs/core";

import {
  getPromoCodeFromLocalStorage,
  removePromoCodeFromLocalStorage,
} from "@/components/helpers/custom-hooks/use-store-promo-code";
import { Box, Flex } from "@/components/layout/flexbox";
import CircleIconButton from "@/components/pieces/form/CircleIconButton";
import { BRAND_PURPLE } from "@/css/constants";
import { RedeemPromoCodeDocument } from "@/graphql";

export default function PromoCodeSelector({ order, loading, setOrder }) {
  const [showingForm, setShowingForm] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [triedCode, setTriedCode] = useState(false);

  const [redeemPromoCode, { loading: submitting }] = useMutation(
    RedeemPromoCodeDocument,
    {
      onCompleted: (r) => {
        if (r.errors) {
          setError(
            "Whoops, something went wrong. Please refresh and try again.",
          );
          return;
        }
        if (r && r.redeemPromoCode && r.redeemPromoCode.error && showingForm) {
          setError(r.redeemPromoCode.error);
        } else {
          setCode("");
          setShowingForm(false);
        }
        const newOrder = r && r.redeemPromoCode && r.redeemPromoCode.order;
        setOrder(newOrder);
      },
    },
  );

  function handleSubmit(codeOverride?: string) {
    setError(null);
    redeemPromoCode({
      variables: {
        orderId: order.id,
        code: codeOverride || code,
      },
    });
  }

  // Check to see if we have a promo code saved in local storage.
  // If so, apply it if we don't have another promo code already
  useEffect(() => {
    const promoCode = getPromoCodeFromLocalStorage();
    if (!promoCode || !order || !order.id) return;
    // if the order already has a promo code, or we tried the
    // promo code but there isn't a promo code on the order,
    // try to remove the code from local storage
    if (order.promoCode || (triedCode && !order.promoCode)) {
      removePromoCodeFromLocalStorage();
      return;
    }
    setTriedCode(true);
    handleSubmit(promoCode);
  }, [order]);

  if (showingForm && order) {
    return (
      <Box>
        <Flex alignItems="center" justifyContent="space-between">
          <Flex flexWrap="wrap">
            <FormGroup
              inline={true}
              label="Promo Code:"
              labelFor="promo-code"
              style={{ margin: 0, alignItems: "center" }}
            >
              <InputGroup
                autoFocus={true}
                value={code}
                name="promo-code"
                placeholder="Enter Promo Code"
                onChange={(e) => setCode(e.target.value)}
                large={true}
                fill={false}
                required={true}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSubmit();
                  }
                }}
              />
            </FormGroup>
            <Box ml={3}>
              <Button
                intent="primary"
                large={true}
                disabled={loading || submitting || !code}
                loading={submitting}
                onClick={() => handleSubmit()}
              >
                Redeem
              </Button>
            </Box>
          </Flex>
          <CircleIconButton
            icon="cross"
            iconSize={24}
            size={36}
            onClick={() => setShowingForm(false)}
          />
        </Flex>
        {error && error.message && (
          <Box mt={3}>
            <Callout intent="warning" title="Could not redeem promo code">
              {error.message}
            </Callout>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <a
      href="#"
      onClick={(e) => {
        e.preventDefault();
        setShowingForm(true);
      }}
    >
      <Box color={BRAND_PURPLE} fontWeight={500}>
        <Icon icon="plus" /> Use{order && order.promoCode && " different"} promo
        code
      </Box>
    </a>
  );
}
